import { useState, useEffect } from "react";
import axios from "axios";
import { Container, Row, Col, Button, Form, Spinner, Card, Modal, InputGroup, Alert } from "react-bootstrap";
import { AgGridReact } from "ag-grid-react";
import { ProgressBar } from "react-bootstrap";

import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-alpine.css'
import 'ag-grid-enterprise'

function LinkFetcher() {
  const [domain, setDomain] = useState("");
  const [loading, setLoading] = useState(false);
  const [pages, setPages] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [crawlMode, setCrawlMode] = useState(null);
  const [selectedPages, setSelectedPages] = useState([]);
  const [domainId, setDomainId] = useState(null);
  const [consolidatedText, setConsolidatedText] = useState("");
  const [consolidating, setConsolidating] = useState(false);
  const [activePage, setActivePage] = useState(null);
  const [consolidationId, setConsolidationId] = useState(null);
  const [crawlDepth, setCrawlDepth] = useState(2);
  const [progressData, setProgressData] = useState({
    crawled: 0,
    discovered: 0,
    progress: 0,
    depth: 0,
    maxDepth: 0,
    currentUrl: "",
    currentTitle: ""
  });
  const [crawlerStarted, setCrawlerStarted] = useState(false);
  const [crawlStatus, setCrawlStatus] = useState("IDLE"); // IDLE, RUNNING, PAUSED
  const [crawlId, setCrawlId] = useState(null);
  const [showVectorModal, setShowVectorModal] = useState(false);

  const [vectorConfig, setVectorConfig] = useState({
    provider: "qdrant",
    url: "",
    apiKey: "",
    collection: "website-kb",
    embeddingModel: "sentence-transformers/all-MiniLM-L6-v2"
  });

  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [uploadingVector, setUploadingVector] = useState(false);
  const [showCrawlModal, setShowCrawlModal] = useState(false);
  const [crawlDecision, setCrawlDecision] = useState(null);

  const BACKEND_URL = process.env.REACT_APP_BASE_URL; // your Express port

  const embeddingModels = [
    {
      value: "sentence-transformers/all-MiniLM-L6-v2",
      label: "MiniLM-L6-v2",
      dimension: 384,
      speed: "Fast"
    },
    {
      value: "sentence-transformers/all-mpnet-base-v2",
      label: "MPNet Base",
      dimension: 768,
      speed: "Medium"
    },
    {
      value: "BAAI/bge-base-en-v1.5",
      label: "BGE Base",
      dimension: 768,
      speed: "Medium"
    },
    {
      value: "BAAI/bge-large-en-v1.5",
      label: "BGE Large",
      dimension: 1024,
      speed: "Slow"
    }
  ];

  const columnDefs = [
    {
      headerName: "",
      checkboxSelection: true,
      headerCheckboxSelection: true,
      width: 50
    },
    {
      headerName: "Links",
      field: "link",
      flex: 1
    }
  ];

  const fetchLinks = async () => {

    setLoading(true);
    setCrawlerStarted(false);
    setPages([]);
    setSelectedPages([]);
    setActivePage(null);

    setConsolidatedText("");
    setConsolidationId(null);

    setDomainId(null);
    setCrawlMode(null);
    setProgress(0);

    setProgressData({
      crawled: 0,
      discovered: 0,
      depth: 0,
      maxDepth: crawlDepth,
      currentUrl: "",
      currentTitle: "",
      progress: 0
    });

    startSSE();

    try {

      const res = await axios.post("/api/fetch-links", {
        domain,
        depth: crawlDepth
      }
      );
      console.log("Fetch response:", res.data);

      setDomainId(res.data.domainId);
      setCrawlId(res.data.crawlId);
      setCrawlMode(res.data.mode);

      if (res.data.mode === "CRAWLED") {
        setCrawlStatus("RUNNING");
      }

      if (res.data.mode === "CACHED") {
        setPages(res.data.data);
        setDomainId(res.data.domainId);
        setCrawlId(res.data.crawlId);
        setCrawlDecision(res.data);
        setShowCrawlModal(true);
        setLoading(false);
        setCrawlerStarted(false);
        setCrawlStatus("IDLE");
      }

    } catch (err) {

      console.error("Crawl failed:", err);

      setLoading(false);
      setCrawlerStarted(false);
    }
  };

  // const fetchLinks = async () => {
  //   setLoading(true);
  //   setPages([]);
  //   setProgress(0);

  //   const sse = startSSE(); // 🔥 WAIT until connected
  //   console.log("sse", sse)
  //   const res = await axios.post("/api/fetch-links", { domain, depth: crawlDepth });
  //   setDomainId(res.data.domainId);
  //   setCrawlMode(res.data.mode);

  //   if (res.data.mode === "CACHED") {
  //     setPages(res.data.data);
  //     setProgress(100);
  //     setLoading(false);
  //   }
  // };

  const normalizeUrl = (value) => {
    let url = value.trim();

    if (!/^https?:\/\//i.test(url)) {
      url = "https://" + url;
    }

    const parsed = new URL(url);
    parsed.protocol = "https:";
    return parsed.origin + parsed.pathname.replace(/\/$/, "");
  };

  const startSSE = () => {
    const normalized = new URL(normalizeUrl(domain)).origin;

    const es = new EventSource(
      `${BACKEND_URL}/api/crawl-progress?domain=${encodeURIComponent(normalized)}`
    );
    ;

    es.onopen = () => {
      console.log("SSE connected");
    };

    es.onmessage = (event) => {
      const data = JSON.parse(event.data);

      if (data.type === "PAGE") {

        const updatedPage = {
          link: data.page.link,
          textcontent: data.page.text,
          title: data.page.title || "",
          depth: data.page.depth,
          consolidatedcontent: data.page.consolidatedcontent || ""
        };

        setPages(prev => {
          const exists = prev.some(p => p.link === updatedPage.link);

          if (exists) {
            return prev.map(p =>
              p.link === updatedPage.link ? updatedPage : p
            );
          }

          return [...prev, updatedPage];
        });

        setActivePage(prev =>
          prev?.link === updatedPage.link
            ? updatedPage
            : prev
        );
      }

      if (data.type === "PROGRESS") {

        setCrawlerStarted(true);

        setProgressData(prev => ({
          crawled: data.crawled ?? prev.crawled,
          discovered: data.discovered ?? prev.discovered,
          progress: data.progress ?? prev.progress,
          depth: data.depth ?? prev.depth,
          maxDepth: data.maxDepth ?? prev.maxDepth,
          currentUrl: data.currentUrl ?? prev.currentUrl,
          currentTitle: data.currentTitle ?? prev.currentTitle
        }));

      }

      if (data.type === "RESUMED") {

        setLoading(true);
        setCrawlStatus("RUNNING");

        return;

      }
      if (data.type === "PAUSED") {

        setLoading(false);
        setCrawlStatus("PAUSED");
        setProgressData(prev => ({
          ...prev
        }));

        return;

      }

      if (data.type === "DONE") {

        setProgress(100);

        setLoading(false);
        setCrawlStatus("IDLE");

        setProgressData(prev => ({
          ...prev,
          progress: 100,
          crawled: data.crawled ?? prev.crawled
        }));

        setCrawlerStarted(false);
        es.close();
      }

      if (data.type === "ERROR") {

        console.error("Crawler error:", data.error);

        setLoading(false);

        es.close();

        return;
      }
    };

    return es; // 🔥 REQUIRED
  };


  const fetchConsolidatedData = async () => {
    try {
      setConsolidating(true);

      const response = await axios.post("/api/consolidate", {
        crawlId,
        links: selectedPages.map(p => p.link)
      });
      const consolidated = response.data.consolidatedText;

      setConsolidatedText(consolidated);

      setPages(prev =>
        prev.map(page =>
          page.link === selectedPages[0].link
            ? { ...page, consolidatedcontent: consolidated }
            : page
        )
      );

      setActivePage(prev =>
        prev && prev.link === selectedPages[0].link
          ? { ...prev, consolidatedcontent: consolidated }
          : prev
      );

    } catch (err) {
      console.error("Consolidation failed", err);
    } finally {
      setConsolidating(false);
    }
  };

  useEffect(() => {
    if (pages.length && !activePage) {
      setActivePage(pages[0]);
    }
  }, [pages]);

  // if (crawlDepth === -1) {
  //   alert("Full crawl may take several minutes and consume high resources.");
  // }

  console.log("progressdata", progressData)

  const pauseCrawl = async () => {
    try {
      await axios.post("/api/pause-crawl", { crawlId });
      setCrawlStatus("PAUSED");
      setLoading(false);
    } catch (err) {
      console.error(err);
    }
  };

  const stopCrawl = async () => {
    try {
      await axios.post("/api/stop-crawl", { crawlId });
      setCrawlStatus("IDLE");
      setLoading(false);
    } catch (err) {
      console.error(err);
    }
  };

  const resumeCrawl = async () => {
    try {
      setLoading(true);
      setCrawlStatus("RUNNING");

      await axios.post("/api/resume-crawl", {
        crawlId
      });


    } catch (err) {
      console.error("Resume failed:", err);
      setLoading(false);
      setCrawlStatus("PAUSED");
    }
  };

  const testVectorConnection = async () => {

    try {

      setTestingConnection(true);

      await axios.post(
        "/api/qdrant/test",
        {
          url: vectorConfig.url,
          apiKey: vectorConfig.apiKey,
          embeddingModel: vectorConfig.embeddingModel
        }
      );

      setConnectionStatus("success");

    } catch {

      setConnectionStatus("error");

    } finally {

      setTestingConnection(false);

    }
  };

  const uploadToQdrant = async () => {
    try {
      setUploadingVector(true);

      let response;

      if (selectedPages.length === 1) {

        response = await axios.post("/api/qdrant/upload", {
          crawlId,
          link: selectedPages[0].link,
          url: vectorConfig.url,
          apiKey: vectorConfig.apiKey,
          collection: vectorConfig.collection,
          embeddingModel: vectorConfig.embeddingModel
        });

      } else {

        response = await axios.post("/api/qdrant/upload/bulk", {
          crawlId,
          links: selectedPages.map(page => ({
            link: page.link
          })),
          url: vectorConfig.url,
          apiKey: vectorConfig.apiKey,
          collection: vectorConfig.collection,
          embeddingModel: vectorConfig.embeddingModel
        });

      }

      const result = response.data;

      if (selectedPages.length === 1) {

        if (result.is_update) {
          alert(
            `Updated Vector Database\n\n` +
            `Deleted ${result.deleted_chunks} old chunks\n` +
            `Uploaded ${result.uploaded_chunks} new chunks`
          );
        } else {
          alert(
            `Vector Database uploaded\n\n` +
            `Uploaded ${result.uploaded_chunks} chunks`
          );
        }

      } else {

        alert(
          `Bulk upload completed\n\n` +
          `Total pages: ${result.total}\n` +
          `Successful: ${result.successful}\n` +
          `Failed: ${result.failed}`
        );

        console.log("Qdrant bulk upload results:", result.results);
      }

      setShowVectorModal(false);

    } catch (err) {

      console.error("Qdrant upload failed:", err);

      const errorMessage =
        err.response?.data?.error ||
        err.response?.data?.detail ||
        err.message ||
        "Upload failed.";

      alert(`Upload failed\n\n${errorMessage}`);

    } finally {

      setUploadingVector(false);
    }
  };


  const startNewCrawl = async () => {
    try {
      setShowCrawlModal(false);

      setLoading(true);
      setCrawlerStarted(false);
      setCrawlMode("CRAWLED");
      setCrawlStatus("RUNNING");

      setPages([]);
      setProgress(0);

      setProgressData({
        crawled: 0,
        discovered: 0,
        progress: 0,
        depth: 0,
        maxDepth: crawlDepth - 1,
        currentUrl: "",
        currentTitle: ""
      });

      startSSE();

      const res = await axios.post("/api/fetch-links", {
        domain,
        depth: crawlDepth,
        forceRecrawl: true
      });

      setDomainId(res.data.domainId);
      setCrawlId(res.data.crawlId);

    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  const resetUI = () => {

    // Close SSE if still connected
    startSSE.current?.close();
    startSSE.current = null;

    setDomain("");

    setLoading(false);
    setCrawlerStarted(false);
    setCrawlStatus("IDLE");

    setPages([]);
    setSelectedPages([]);

    setActivePage(null);

    setProgress(0);

    setProgressData({
      crawled: 0,
      discovered: 0,
      progress: 0,
      depth: 0,
      maxDepth: 0,
      currentUrl: "",
      currentTitle: ""
    });

    setDomainId(null);
    setCrawlId(null);
    setCrawlMode(null);

    setConsolidatedText("");
    setConsolidationId(null);

  };

  const downloadSelectedTxt = async () => {
    const response = await fetch("/api/raw-content/download/txt/bulk", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        crawlId,
        links: selectedPages,
      }),
    });

    const blob = await response.blob();

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "raw-content-txt.zip";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const downloadSelectedPdf = async () => {
    const response = await fetch("/api/raw-content/download/pdf/bulk", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        crawlId,
        links: selectedPages,
      }),
    });

    const blob = await response.blob();

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "raw-content-txt.zip";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <>
      {loading && !crawlerStarted && (
        <div className="crawler-loading-overlay">

          <div className="crawler-loading-box">

            <Spinner
              animation="border"
              variant="light"
              style={{
                width: "3rem",
                height: "3rem"
              }}
            />

            <h5 className="mt-3 mb-2">
              Starting crawler...
            </h5>

            <p className="mb-0">
              Connecting to the crawler and preparing the website.
            </p>

          </div>

        </div>
      )}

      <Container fluid className="mt-3">
        <Card className="p-3 mb-3">
          <Row className="align-items-center">
            <Col md={8}>
              <Form.Control
                value={domain}
                onChange={e => setDomain(e.target.value)}
                placeholder="https://example.com"
              />
            </Col>
            <Col md={2}>
              <div className="d-grid gap-1">

                {crawlStatus === "IDLE" && (
                  <>
                    <Button onClick={fetchLinks} className="w-100" > Fetch </Button>
                    <Button variant="outline-secondary" onClick={resetUI} > Reset </Button>
                  </>
                )}

                {crawlStatus === "RUNNING" && (
                  <>
                    <Button
                      variant="warning"
                      onClick={pauseCrawl}
                      className="w-100"
                    >
                      <icon name="pause" inline /> Pause
                    </Button>

                    <Button
                      variant="danger"
                      onClick={stopCrawl}
                      className="w-100"
                    >
                      <icon name="square" inline /> Stop
                    </Button>
                  </>
                )}

                {crawlStatus === "PAUSED" && (
                  <>
                    <Button variant="success" onClick={resumeCrawl} className="w-100">
                      <icon name="play" inline /> Resume
                    </Button>

                    <Button variant="danger" onClick={stopCrawl} className="w-100">
                      <icon name="square" inline /> Stop
                    </Button>
                    <Button variant="outline-secondary" onClick={resetUI}>
                      Reset
                    </Button>
                  </>
                )}
                <Button variant="success" onClick={downloadSelectedTxt} className="w-100 mt-2">
                  <icon name="play" inline /> Download Bulk Text
                </Button>

              </div>
            </Col>
            <Col md={2}>
              <Form.Select
                value={crawlDepth}
                onChange={e => setCrawlDepth(Number(e.target.value))}
                className="w-100 mb-5"
                disabled={uploadingVector}
              >
                <option value={1}>Depth 1 – Homepage only</option>
                <option value={2}>Depth 2 – Shallow</option>
                <option value={3}>Depth 3 – Recommended</option>
                <option value={4}>Depth 4 – Deep</option>
                <option value={5}>Depth 5 – Very Deep</option>
                <option value={-1}>Full site</option>
              </Form.Select>

              <Button variant="success" onClick={downloadSelectedPdf} className="w-100">
                <icon name="play" inline /> Download Bulk PDF
              </Button>
            </Col>

          </Row>

          <Row>
            <Col md={8}>

              {crawlMode === "CRAWLED" &&
                (crawlStatus === "RUNNING" || crawlStatus === "PAUSED") && (
                  <Row className="mt-3">
                    <Col md={12}>
                      <Card className="p-3">

                        <div className="d-flex justify-content-between mb-2">
                          <strong>
                            {crawlStatus === "PAUSED"
                              ? "Crawl Paused"
                              : "Crawling website"}
                          </strong>

                          <span>{progressData.crawled} pages crawled</span>
                        </div>

                        <ProgressBar
                          now={progressData.progress}
                          animated={crawlStatus === "RUNNING"}
                          striped={crawlStatus === "RUNNING"}
                          label={`${progressData.progress}%`}
                          className="mb-3 mt-3"
                        />

                        <div className="d-flex justify-content-between mb-2">
                          <span>
                            Depth <strong>{progressData.depth}</strong> / {progressData.maxDepth}
                          </span>

                          <span>{progressData.discovered} pages discovered</span>
                        </div>

                        {progressData.currentTitle && (
                          <div className="mt-2">
                            <strong>Current page:</strong> {progressData.currentTitle}
                          </div>
                        )}

                        <small className="text-muted d-block mt-1">
                          {progressData.currentUrl}
                        </small>

                      </Card>
                    </Col>
                  </Row>
                )}

              {crawlMode === "CRAWLED" &&
                crawlStatus === "IDLE" &&
                progressData.progress === 100 && (
                  <ProgressBar
                    now={100}
                    label="Completed"
                    className="mb-3 mt-3"
                  />
                )}

              {crawlMode === "CACHED" && (
                <ProgressBar
                  now={100}
                  label="Completed"
                  className="mb-3 mt-3"
                />
              )}

            </Col>
            <Col md={4}>



            </Col>
          </Row>
        </Card>

        <Row style={{ height: "70vh" }}>

          {/* LEFT COLUMN – AG GRID */}
          <Col md={4}>
            <div className="ag-theme-alpine" style={{ height: "100%", width: "100%" }}>
              <AgGridReact
                rowData={pages}
                columnDefs={columnDefs}
                getRowId={params => params.data.link}
                rowSelection="multiple"
                // In case multiselction code for consolidation wants to revert the chnages uncomments this 2 and remove set  setSelectedPages(rows); from onRowCliclked
                suppressRowClickSelection={true}
                onSelectionChanged={(event) => {
                  const rows = event.api.getSelectedRows();
                  setSelectedPages(rows);

                }}
                onRowClicked={(event) => {
                  // const rows = event.api.getSelectedRows();
                  // setSelectedPages(rows);
                  setActivePage(event.data);
                  setConsolidatedText("");

                }}
              />

            </div>
          </Col>

          {/* RIGHT COLUMN – CONTENT */}
          <Col md={8}>
            <Card style={{ height: "80vh" }}>
              <Card.Header className="d-flex align-items-center"><span className="fw-semibold">Selected Content</span>
                <div className="ms-auto d-flex gap-2">
                  <Button
                    onClick={() =>
                      window.open(
                        `${BACKEND_URL}/api/raw-content/download/pdf?crawlId=${crawlId}&link=${encodeURIComponent(activePage.link)}`,
                        "_blank"
                      )
                    }
                    className="btn-sm"
                    variant="outline-primary"
                  >
                    Download PDF
                  </Button>

                  <Button
                    onClick={() =>
                      window.open(
                        `${BACKEND_URL}/api/raw-content/download/txt?crawlId=${crawlId}&link=${encodeURIComponent(activePage.link)}`,
                        "_blank"
                      )
                    }
                    className="btn-sm"
                    variant="outline-primary"
                  >
                    Download TXT
                  </Button>
                  <Button
                    className="btn-sm"
                    onClick={() => setShowVectorModal(true)}
                    disabled={
                      !selectedPages.length ||
                      !selectedPages.every(page => page.textcontent)
                    }
                  >
                    <icon name="database" inline /> Send {selectedPages.length} Page
                    {selectedPages.length !== 1 ? "s" : ""} to Vector DB
                  </Button>
                  {/* <Button
                    variant="success"
                    disabled={!selectedPages.length || consolidating || selectedPages.length > 1}
                    onClick={fetchConsolidatedData}
                    className="btn-sm"
                  >
                    {consolidating ? "Consolidating..." : "Create KB"}
                  </Button> */}
                </div>
              </Card.Header>
              <Card.Body
                style={{
                  overflowY: "auto",
                  maxHeight: "100%",
                }}
              >

                <pre
                  style={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    margin: 0,
                  }}
                >
                  {activePage?.textcontent || "Select a page to preview"}

                </pre>

              </Card.Body>
            </Card>
          </Col>
          {/* <Col md={4}>
            <Card style={{ height: "80vh" }}>
              <Card.Header className="d-flex align-items-center">
                <span className="fw-semibold">Knowledge Base Preview</span>

                <div className="ms-auto d-flex gap-2">
                  <Button
                    onClick={() =>
                      window.open(
                        `${BACKEND_URL}/api/consolidation/download/pdf?crawlId=${crawlId}&link=${encodeURIComponent(activePage.link)}`,
                        "_blank"
                      )
                    }
                    className="btn-sm"
                    variant="outline-secondary"
                    disabled={!activePage?.consolidatedcontent && !consolidatedText}
                  >
                    Download PDF
                  </Button>

                  <Button
                    onClick={() =>
                      window.open(
                        `${BACKEND_URL}/api/consolidation/download/txt?crawlId=${crawlId}&link=${encodeURIComponent(activePage.link)}`,
                        "_blank"
                      )
                    }
                    className="btn-sm"
                    variant="outline-secondary"
                    disabled={!activePage?.consolidatedcontent && !consolidatedText}
                  >
                    Download TXT
                  </Button>
                  <Button
                    className="btn-sm"
                    onClick={() => setShowVectorModal(true)}
                    disabled={!activePage?.consolidatedcontent}
                  >
                    <icon name="database" inline /> Send to Vector DB
                  </Button>
                </div>
              </Card.Header>

              <Card.Body style={{ height: "100%", overflowY: "auto" }}>
                <pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>
                  {activePage?.consolidatedcontent ||
                    consolidatedText ||
                    "Select a page and generate or view its knowledge base."}
                </pre>
              </Card.Body>
            </Card>
          </Col> */}
        </Row>

        <Modal
          show={showCrawlModal}
          onHide={() => setShowCrawlModal(false)}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Crawl Options</Modal.Title>
          </Modal.Header>

          <Modal.Body>

            {crawlDecision?.action === "VIEW_OR_RECRAWL" && (
              <>
                <p><strong>This website has already been crawled.</strong></p>
                <p>Version: {crawlDecision.version}</p>
                <p>Depth: {crawlDecision.requestedDepth}</p>
                <p>Pages: {crawlDecision.totalLinks}</p>
              </>
            )}

            {crawlDecision?.action === "DEPTH_CHANGED" && (
              <>
                <p><strong>This website already exists.</strong></p>
                <p>Existing depth: {crawlDecision.requestedDepth}</p>
                <p>Selected depth: {crawlDepth}</p>
              </>
            )}

            {crawlDecision?.action === "RESUME" && (
              <>
                <p><strong>A paused crawl was found.</strong></p>
                <p>Version: {crawlDecision.version}</p>
                <p>Depth: {crawlDecision.requestedDepth}</p>
              </>
            )}

          </Modal.Body>

          <Modal.Footer>

            {crawlDecision?.action === "RESUME" ? (
              <>
                <Button variant="secondary" onClick={() => setShowCrawlModal(false)}>
                  Close
                </Button>

                <Button
                  onClick={() => {
                    setShowCrawlModal(false);
                    resumeCrawl();
                  }}
                >
                  Resume Crawl
                </Button>
              </>
            ) : (
              <>
                <Button variant="secondary" onClick={() => setShowCrawlModal(false)}>
                  Use Existing
                </Button>

                <Button onClick={startNewCrawl}>
                  Re-Crawl
                </Button>
              </>
            )}

          </Modal.Footer>
        </Modal>

      </Container >

      <Modal
        show={showVectorModal}
        onHide={() => {
          setShowVectorModal(false);
          setConnectionStatus(null);
        }}
        centered
        size="lg"
      >
        <Modal.Body className="vector-modal">

          <div className="text-center mb-4">

            <div className="vector-icon mb-3">
              <icon name="database" size="2xl" />
            </div>

            <h4>Connect to Qdrant</h4>

            <p className="text-secondary mb-0">
              Send the crawled page content for {selectedPages.length}{" "}
              {selectedPages.length === 1 ? "selected page" : "selected pages"} directly
              into your vector database.
            </p>

          </div>

          {connectionStatus === "success" && (
            <Alert variant="success" className="d-flex align-items-center gap-2">
              <icon name="circle-check" color="success" />
              Connected to Qdrant successfully.
            </Alert>
          )}

          {connectionStatus === "error" && (
            <Alert variant="danger" className="d-flex align-items-center gap-2">
              <icon name="circle-x" color="danger" />
              Unable to connect.
            </Alert>
          )}

          <Form>

            <Form.Group className="mb-3">

              <Form.Label>Embedding Model</Form.Label>

              <Form.Select
                value={vectorConfig.embeddingModel}
                onChange={(e) =>
                  setVectorConfig({
                    ...vectorConfig,
                    embeddingModel: e.target.value
                  })
                }
                disabled={uploadingVector}
              >
                {embeddingModels.map(model => (
                  <option
                    key={model.value}
                    value={model.value}
                  >
                    {model.label} ({model.dimension}D • {model.speed})
                  </option>
                ))}
              </Form.Select>

              <Form.Text className="text-secondary">
                The selected model determines the vector dimension.
              </Form.Text>

            </Form.Group>

            <Form.Group className="mb-3">

              <Form.Label>Qdrant URL</Form.Label>

              <InputGroup>

                <InputGroup.Text>
                  <icon name="globe" />
                </InputGroup.Text>

                <Form.Control
                  placeholder="https://your-cluster.qdrant.tech"
                  value={vectorConfig.url}
                  onChange={(e) =>
                    setVectorConfig({
                      ...vectorConfig,
                      url: e.target.value
                    })
                  }
                  disabled={uploadingVector}
                />

              </InputGroup>

            </Form.Group>

            <Form.Group className="mb-3">

              <Form.Label>API Key</Form.Label>

              <InputGroup>

                <InputGroup.Text>
                  <icon name="key" />
                </InputGroup.Text>

                <Form.Control
                  type="password"
                  placeholder="Enter API key"
                  value={vectorConfig.apiKey}
                  onChange={(e) =>
                    setVectorConfig({
                      ...vectorConfig,
                      apiKey: e.target.value
                    })
                  }
                  disabled={uploadingVector}
                />

              </InputGroup>

            </Form.Group>

            <Form.Group className="mb-4">

              <Form.Label>Collection Name</Form.Label>

              <InputGroup>

                <InputGroup.Text>
                  <icon name="database" />
                </InputGroup.Text>

                <Form.Control
                  value={vectorConfig.collection}
                  onChange={(e) =>
                    setVectorConfig({
                      ...vectorConfig,
                      collection: e.target.value
                    })
                  }
                  disabled={uploadingVector}
                />

              </InputGroup>

            </Form.Group>

            <div className="d-flex justify-content-between">

              <Button
                variant="outline-secondary"
                onClick={testVectorConnection}
                disabled={testingConnection || uploadingVector}
              >
                {testingConnection ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Testing...
                  </>
                ) : (
                  <>
                    <icon name="plug" inline /> Test Connection
                  </>
                )}
              </Button>

              {connectionStatus === "success" && (
                <Button
                  onClick={uploadToQdrant}
                  disabled={uploadingVector}
                >
                  {uploadingVector ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      Send {selectedPages.length}{" "}
                      {selectedPages.length === 1 ? "Page" : "Pages"} to Qdrant
                    </>
                  )}
                </Button>
              )}

            </div>

          </Form>

        </Modal.Body>
      </Modal>
    </>
  );
}

export default LinkFetcher;
